import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BfIconChevDown } from './BfIcons';
import { bfHaptic } from './BfHooks';
import { fetchLanguagesList } from '../../../utils/bibleLocaleSync';

/**
 * BfTranslationSwitcher — простой editorial-чип в шапке + маленький
 * выпадающий список переводов (как у легаси LanguageSelector), стилизованный
 * под BF.
 *
 * Поведение зеркалит легаси:
 *  - показывает переводы ТЕКУЩЕГО языка (`bibleLanguage` из localStorage,
 *    либо язык, которому принадлежит currentTranslation).
 *  - если переводов <=1 — компонент ничего не рендерит.
 *  - тап вне дропдауна или скролл — закрывают.
 *  - выбор перевода → onChange (caller делает crossfade + navigate).
 */

function fullLabel(translation) {
  if (!translation) return '';
  return translation.name || translation.shortName || translation.id || '';
}

/**
 * Фронтовый конфиг сокращений переводов. API не всегда возвращает корректный
 * shortName (например, у `nrt` приходит полное «Новый русский перевод»),
 * поэтому короткий лейбл резолвится локально по id.
 *
 * Источник истины: id → сокращённое имя.
 */
const TRANSLATION_SHORT_LABELS = {
  // Русские
  synodal: 'СИН',
  nrt: 'НРП',
  // Английские
  kjv: 'KJV',
  web: 'WEB',
  niv: 'NIV',
  esv: 'ESV',
  nasb: 'NASB',
  net: 'NET',
  asv: 'ASV',
  // Прочие
  lut1912: 'LUT',
  neg: 'NEG',
  rv1909: 'RV',
  almeida: 'ARC',
  irv1990: 'RIV',
  pbg: 'PBG',
  kor: 'KOR',
};

const MAX_CHIP_LEN = 6;

function shortChipLabel(translation, fallbackId) {
  const id = String(translation?.id || fallbackId || '').toLowerCase();
  // 1. Локальный конфиг — высший приоритет, надёжный.
  if (id && TRANSLATION_SHORT_LABELS[id]) return TRANSLATION_SHORT_LABELS[id];

  // 2. shortName из API — но только если реально короткий.
  const apiShort = String(translation?.shortName || '').trim();
  if (apiShort && apiShort.length <= MAX_CHIP_LEN) {
    return apiShort.toUpperCase();
  }

  // 3. Сокращение в скобках, e.g. "King James Version (KJV)".
  const name = String(translation?.name || '');
  const inParens = name.match(/\(([^)]+)\)/);
  if (inParens && inParens[1].trim().length <= MAX_CHIP_LEN) {
    return inParens[1].trim().toUpperCase();
  }

  // 4. Само имя, если оно короткое.
  if (name && name.length <= MAX_CHIP_LEN) return name.toUpperCase();

  // 5. Последний шанс — id перевода в верхнем регистре.
  return String(translation?.id || fallbackId || '').toUpperCase();
}

export default function BfTranslationSwitcher({ currentTranslation, onChange }) {
  const [open, setOpen] = useState(false);
  const [languages, setLanguages] = useState(null);
  const rootRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetchLanguagesList().then((langs) => {
      if (!cancelled) setLanguages(langs || []);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Язык текущего перевода — приоритет; иначе localStorage; иначе первый язык.
  const currentLanguage = useMemo(() => {
    if (!languages?.length) return null;
    const owner = languages.find(
      (lang) =>
        Array.isArray(lang.translations) &&
        lang.translations.some((t) => t.id === currentTranslation),
    );
    if (owner) return owner;
    const stored =
      typeof window !== 'undefined'
        ? (localStorage.getItem('bibleLanguage') || 'en').toLowerCase()
        : 'en';
    return languages.find((l) => l.code === stored) || languages[0] || null;
  }, [languages, currentTranslation]);

  const availableTranslations = useMemo(
    () => (currentLanguage?.translations || []),
    [currentLanguage],
  );

  const currentTr = useMemo(
    () => availableTranslations.find((t) => t.id === currentTranslation) || null,
    [availableTranslations, currentTranslation],
  );

  const chipLabel = useMemo(
    () => shortChipLabel(currentTr, currentTranslation),
    [currentTr, currentTranslation],
  );

  // Закрытие: клик вне + scroll (как у легаси).
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('touchstart', onDocClick, { passive: true });
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('touchstart', onDocClick);
      document.removeEventListener('scroll', onScroll, { capture: true });
    };
  }, [open]);

  const handleSelect = useCallback(
    (translationId) => {
      if (translationId === currentTranslation) {
        setOpen(false);
        return;
      }
      bfHaptic('light');
      setOpen(false);
      onChange?.(translationId);
    },
    [currentTranslation, onChange],
  );

  // Не показываем чип, если переключать нечего (как делал легаси).
  if (languages && availableTranslations.length <= 1) return null;

  return (
    <div className="bf-tr-dd" ref={rootRef}>
      <button
        type="button"
        className="bf-tr-chip"
        onClick={() => {
          bfHaptic('light');
          setOpen((v) => !v);
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={currentTr ? fullLabel(currentTr) : chipLabel}
      >
        <span className="bf-tr-chip__label">{chipLabel}</span>
        <span
          className={`bf-tr-chip__chev${open ? ' is-open' : ''}`}
          aria-hidden="true"
        >
          <BfIconChevDown />
        </span>
      </button>

      {open && availableTranslations.length > 0 && (
        <ul className="bf-tr-dd__menu" role="listbox" aria-label="Перевод">
          {availableTranslations.map((tr) => {
            const active = tr.id === currentTranslation;
            return (
              <li key={tr.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  className={`bf-tr-dd__item${active ? ' is-active' : ''}`}
                  onClick={() => handleSelect(tr.id)}
                  title={fullLabel(tr)}
                >
                  <span className="bf-tr-dd__item-name">{fullLabel(tr)}</span>
                  {active && (
                    <span className="bf-tr-dd__item-check" aria-hidden="true">
                      ✓
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
